import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FilterValueInput } from './FilterValueInput';
import {
  FilterCategory,
  FilterFieldMeta,
  ActiveFilter,
  FilterValue,
  FilterRecord,
} from '@/types/filterBuilder';
import { cn } from '@/lib/utils';

interface AllFiltersPanelProps {
  categories: FilterCategory[];
  onApplyFilters: (filters: FilterRecord) => void;
}

// Get default value for a filter type
function getDefaultValue(field: FilterFieldMeta): FilterValue {
  switch (field.inputType) {
    case 'text':
      return '';
    case 'number':
      return null;
    case 'number-range':
      return { min: undefined, max: undefined };
    case 'date':
      return '';
    case 'date-range':
      return { start: undefined, end: undefined };
    case 'boolean':
      return true;
    case 'select':
      return '';
    case 'multi-select':
      return [];
    default:
      return null;
  }
}

export function AllFiltersPanel({
  categories,
  onApplyFilters,
}: AllFiltersPanelProps) {
  const [activeFilters, setActiveFilters] = useState<Record<string, FilterValue>>({});
  const [selectedCategory, setSelectedCategory] = useState<string>(categories[0]?.id ?? '');

  const currentCategory = categories.find((c) => c.id === selectedCategory);

  const handleFilterChange = (key: string, value: FilterValue) => {
    setActiveFilters((prev) => {
      const next = { ...prev };
      // Remove the filter if value is empty/default
      if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  };

  const handleClearAll = () => {
    setActiveFilters({});
  };

  const handleApply = () => {
    const filters: FilterRecord = {};

    for (const category of categories) {
      for (const field of category.filters) {
        const value = activeFilters[field.key];
        if (value === null || value === undefined || value === '') continue;
        if (Array.isArray(value) && value.length === 0) continue;

        switch (field.inputType) {
          case 'date-range': {
            const rangeValue = value as { start?: string; end?: string };
            if (field.rangeKeys) {
              if (rangeValue.start) filters[field.rangeKeys.start] = rangeValue.start;
              if (rangeValue.end) filters[field.rangeKeys.end] = rangeValue.end;
            }
            break;
          }
          case 'number-range': {
            const rangeValue = value as { min?: number; max?: number };
            if (field.rangeKeys) {
              if (rangeValue.min !== undefined) filters[field.rangeKeys.start] = rangeValue.min;
              if (rangeValue.max !== undefined) filters[field.rangeKeys.end] = rangeValue.max;
            }
            break;
          }
          case 'multi-select': {
            filters[field.key] = (value as string[]).join(',');
            break;
          }
          default: {
            filters[field.key] = value;
          }
        }
      }
    }

    onApplyFilters(filters);
  };

  const activeFilterCount = Object.keys(activeFilters).length;

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col md:flex-row gap-3 md:gap-4 flex-1 min-h-0">
        {/* Category sidebar */}
        <div className="w-full md:w-48 flex-shrink-0 overflow-x-auto md:overflow-x-visible md:overflow-y-auto border-b md:border-b-0 md:border-r pb-2 md:pb-0 md:pr-2">
          <nav className="flex gap-1 md:block md:space-y-1">
            {categories.map((category) => {
              const categoryFilterCount = category.filters.filter(
                (f) => activeFilters[f.key] !== undefined
              ).length;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={cn(
                    'w-auto md:w-full whitespace-nowrap text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center justify-between',
                    selectedCategory === category.id
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <span>{category.title}</span>
                  {categoryFilterCount > 0 && (
                    <span className="ml-2 bg-primary/20 text-primary text-xs px-1.5 py-0.5 rounded-full">
                      {categoryFilterCount}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Filter inputs for selected category */}
        <div className="flex-1 overflow-y-auto max-h-[45dvh] md:max-h-[50vh] pr-1 md:pr-2">
          {currentCategory ? (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-foreground">{currentCategory.title}</h3>
              {currentCategory.filters.map((field) => (
                <div key={field.key} className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">
                    {field.label}
                  </label>
                  {field.description && (
                    <p className="text-xs text-muted-foreground">{field.description}</p>
                  )}
                  <FilterValueInput
                    field={field}
                    value={activeFilters[field.key] ?? getDefaultValue(field)}
                    onChange={(value) => handleFilterChange(field.key, value)}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <p>Select a category to see available filters.</p>
            </div>
          )}
        </div>
      </div>

      {/* Apply / Clear buttons */}
      <div className="flex flex-wrap gap-2 justify-between items-center pt-4 mt-4 border-t flex-shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearAll}
          disabled={activeFilterCount === 0}
          className="text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="h-4 w-4 mr-1" />
          Clear All
        </Button>
        <Button onClick={handleApply} disabled={activeFilterCount === 0}>
          Apply {activeFilterCount > 0 ? `${activeFilterCount} Filter${activeFilterCount !== 1 ? 's' : ''}` : 'Filters'}
        </Button>
      </div>
    </div>
  );
}

export default AllFiltersPanel;
